<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header
        variant="buttons"
        :saving="loading"
        @cancel="closeModal()"
        @submit="save"
      >
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-12">
        <div class="flex flex-col w-full">
          <form-card :title="$t('general')" :description="$t(tKey + '.description')">
            <div class="flex flex-col gap-y-5">
              <Multiselect
                v-model="rawVisibilityLocation.region"
                :error="errors?.region"
                :options="regions"
                :label="$t('mounting.region')"
                class="w-full"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
                @select="setMapCenter()"
              />
              <FormInput
                v-model="rawVisibilityLocation.name"
                :error="errors?.name"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('pages.visibility.location.name')"
                :placeholder="$t('pages.visibility.location.name')"
              />
              <LocationMap
                ref="map"
                v-model="rawVisibilityLocation.gisLocation"
                v-model:radius="rawVisibilityLocation.radius"
              />
              <div class="flex gap-x-5">
                <FormInput
                  :model-value="rawVisibilityLocation.gisLocation?.lat"
                  :error="errors?.gisLocation?.lat"
                  class="w-1/2"
                  type="text"
                  :label="$t('lat')"
                  pattern="^\d*\.?\d*$"
                  @update:model-value="
                    setCoords(parseFloat($event), rawVisibilityLocation.gisLocation?.long)
                  "
                />
                <FormInput
                  :model-value="rawVisibilityLocation.gisLocation?.long"
                  :error="errors?.gisLocation?.long"
                  class="w-1/2"
                  type="text"
                  :label="$t('long')"
                  pattern="^\d*\.?\d*$"
                  @update:model-value="
                    setCoords(rawVisibilityLocation.gisLocation?.lat, parseFloat($event))
                  "
                />
              </div>
              <FormInput
                v-model="rawVisibilityLocation.radius"
                :error="errors?.radius"
                class="col-start-1 col-end-2"
                type="number"
                :label="$t('visibility.location.radius')"
                :placeholder="$t('visibility.location.radius')"
              />
              <FormInput
                v-model="rawVisibilityLocation.quota"
                :error="errors?.quota"
                class="col-start-1 col-end-2"
                type="number"
                :label="$t('visibility.location.quota')"
                :placeholder="$t('visibility.location.quota')"
              />
              <Multiselect
                v-model="rawVisibilityLocation.status"
                :error="errors?.status"
                :options="fetchStatus"
                :label="$t('pages.visibility.bicycle.status')"
                class="w-full"
                :can-deselect="false"
                :can-clear="false"
                :searchable="true"
              />
              <FormInput
                v-model="rawVisibilityLocation.comment"
                :error="errors?.comment"
                class="col-start-1 col-end-2"
                type="text"
                :label="$t('pages.visibility.location.comment')"
                :placeholder="$t('pages.visibility.location.comment')"
              />
            </div>
          </form-card>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import {
  bool, number, object, string,
} from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import FormInput from '@utils/inputs/Input.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { syncRef } from '@hooks/state';
import LocationMap from '@utils/maps/PickLocationRadiusMap.vue';
import { collOptionsProvider, enumOptionsProvider } from '@utils/table/hooks/provider';
import FormCard from '@utils/FormCard.vue';
import {
  type Region,
  type VisibilityLocation,
  VisibilityLocationStatus,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  visibilityLocation?: VisibilityLocation;
}

const emit = defineEmits<{(e: 'update:open', value: boolean): void;
  (e: 'update:visibilityLocation', value?: VisibilityLocation): void;
  (e: 'saved'): void;
}>();
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalVisibilityLocation = useVModel(props, 'visibilityLocation', emit);

const isNew = computed(() => !props.visibilityLocation);
const tKey = computed(() =>
  (isNew.value ? 'pages.visibility.location.modal.new' : 'pages.visibility.location.modal.edit'));

const { t } = useI18n();

const notify = useNotify();

const initialData = (visibilityLocation?: VisibilityLocation) =>
  (visibilityLocation
    ? { ...visibilityLocation }
    : {
      name: '',
      gisLocation: {
        lat: 0,
        long: 0,
      },
      region: undefined,
      active: false,
      quota: 0,
      comment: '',
      status: VisibilityLocationStatus.INACTIVE,
      radius: 20,
    });

const fetchStatus = enumOptionsProvider('/enums/VisibilityLocationStatus', (item) => ({
  value: item,
  label: t(`pages.visibility.bicycle.visibilityLocationStatus.${item}`),
}));

const fetchRegions = collOptionsProvider<Region>(
  '/regions',
  {
    sort: {
      sortKey: 'name',
      sortDirection: 'ASC',
    },
  },
  (value) => ({
    label: value.name,
    value: value['@id'],
    gisLocation: value.gisLocation,
  }),
);
const loadingRegions = ref(false);
const regions = ref<
  { label: string; value: string | null; gisLocation?: { lat: number; long: number } }[]
>([]);
const loadRegions = async () => {
  loadingRegions.value = true;
  regions.value = await fetchRegions();
  loadingRegions.value = false;
};
loadRegions();

const rawVisibilityLocation = syncRef(internalVisibilityLocation, (visibilityLocation) => ({
  ...initialData(visibilityLocation),
  region: visibilityLocation?.region?.['@id'],
}));

const {
  data: visibilityLocation,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    name: string().nullable().required(t('validation.notEmpty')),
    region: string().nullable().required(t('validation.notEmpty')),
    gisLocation: object({
      lat: number().typeError(t('validation.number')).nullable().required(t('validation.notEmpty')),
      long: number()
        .typeError(t('validation.number'))
        .nullable()
        .required(t('validation.notEmpty')),
    }),
    status: string().nullable().required(t('validation.notEmpty')),
    radius: number()
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .typeError(t('validation.number'))
      .nullable()
      .required(t('validation.notEmpty')),
    quota: number()
      .positive(t('validation.positive'))
      .integer(t('validation.integer'))
      .typeError(t('validation.number'))
      .nullable()
      .required(t('validation.notEmpty')),
    active: bool().nullable(),
    comment: string(),
  }),
  rawVisibilityLocation,
);

function resetData() {
  rawVisibilityLocation.value = {
    ...initialData(),
  } as unknown as (typeof visibilityLocation)['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
  internalVisibilityLocation.value = undefined;
}

const map = ref();

const setMapCenter = () => {
  const gisLocation = regions.value.find(
    (region) => region.value === visibilityLocation.value?.region,
  )?.gisLocation;
  map.value?.moveToCenter(gisLocation?.lat, gisLocation?.long);
};

const setCoords = (lat: number | undefined, long: number | undefined) => {
  rawVisibilityLocation.value.gisLocation = {
    lat: lat || 0,
    long: long || 0,
  };
};

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.visibilityLocation;
    const url = edit ? props.visibilityLocation['@id'] : '/visibility_locations';

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        name: visibilityLocation.value.name,
        region: visibilityLocation.value.region,
        gisLocation: visibilityLocation.value.gisLocation,
        status: visibilityLocation.value.status,
        radius: visibilityLocation.value.radius,
        quota: visibilityLocation.value.quota,
        comment: visibilityLocation.value.comment,
      },
    });

    notify?.({
      variant: 'success',
      title: t(`${tKey.value}.saveSuccess`),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t(`${tKey.value}.saveError`),
    });
  }
});
</script>
